import React, { useRef } from 'react';
import { AGENT_STATUS_LIST, AGENT_STATUS_COLORS, SIP_STATUS_COLORS } from './pbxConstants';

const styles = {
  overlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: '10% / 5%',          // 👈 matches phone screen radius
    overflow: 'hidden',
    zIndex: 10,
    pointerEvents: 'none',
  },
  overlayActive: { pointerEvents: 'auto' },
  backdrop: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    background: `
      radial-gradient(ellipse at 30% 20%, rgba(120, 80, 180, 0.35), transparent 50%),
      radial-gradient(ellipse at 80% 60%, rgba(50, 120, 200, 0.3), transparent 50%),
      radial-gradient(ellipse at 50% 100%, rgba(200, 60, 100, 0.25), transparent 50%),
      rgba(20, 20, 30, 0.55)
    `,
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    transition: 'opacity 0.25s ease',
  },
  panel: {
    position: 'absolute', top: 0, left: 0, right: 0,
    padding: '44px 12px 20px',
    willChange: 'transform', touchAction: 'none',
  },
  grabber: {
    position: 'absolute', top: '14px', left: '50%',
    transform: 'translateX(-50%)',
    width: '40px', height: '5px',
    background: 'rgba(255, 255, 255, 0.55)',
    borderRadius: '3px',
    transition: 'opacity 0.2s ease',
  },

  row: {
    display: 'grid',
    gridTemplateColumns: '1.3fr 1fr',
    gap: '8px',
    marginBottom: '8px',
  },

  row2: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '8px',
    marginBottom: '8px',
  },

  tile: {
    background: 'rgba(80, 80, 95, 0.55)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },

  infoRow: { display: 'flex', alignItems: 'center', gap: '10px' },

  infoIcon: {
    width: '34px', height: '34px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s ease, box-shadow 0.2s ease',
    flexShrink: 0,
  },
  infoText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    lineHeight: 1,
    minWidth: 0,
  },
  infoLabel: {
    fontSize: '10px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.65)',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  infoValue: {
    fontSize: '12px', fontWeight: 600, color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  },

  rowButton: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  sipIconTile: {
    background: 'rgba(80, 80, 95, 0.55)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gap: '8px',
    alignItems: 'center',
    justifyItems: 'center',
  },
  sipBtn: {
    background: 'transparent', border: 'none',
    cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  },
  sipBtnCircle: {
    width: '44px', height: '44px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.12s ease, box-shadow 0.15s ease, background 0.2s ease',
  },

  brightnessTile: {
    background: 'rgba(80, 80, 95, 0.55)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '20px',
    overflow: 'hidden',
    position: 'relative',
    cursor: 'pointer',
    touchAction: 'none',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '14px',
    minHeight: '160px',
  },
  brightnessFill: {
    position: 'absolute',
    left: 0, right: 0, bottom: 0,
    background: '#fff',
    pointerEvents: 'none',
    borderRadius: '0 0 20px 20px',
  },
  brightnessIcon: {
    position: 'relative',
    zIndex: 2,
    mixBlendMode: 'difference',
    pointerEvents: 'none',
  },
};

const statusColors = AGENT_STATUS_COLORS;

const statusList = AGENT_STATUS_LIST;

function PBXControlCenter({
  isOpen, onClose, dragOffset, isDragging,
  sipStatus, onSipConnect, onSipDisconnect, onSipBreak,
  ringtoneOn, onRingtoneToggle,
  agentStatus, onAgentStatusChange,
  keypadSound, onKeypadSoundToggle,
  theme, onThemeToggle,
  brightness, onBrightnessChange,
  onDismissDrag,
}) {
  const brightnessRef = useRef(null);
  const draggingBrightnessRef = useRef(false);

  const PANEL_HEIGHT = 400;

  const updateVerticalSlider = (clientY) => {
    const el = brightnessRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
    onBrightnessChange(Math.round(pct * 100));
  };

  const sliderHandlers = {
    onPointerDown: (e) => {
      e.preventDefault();
      e.stopPropagation();
      draggingBrightnessRef.current = true;
      updateVerticalSlider(e.clientY);
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e) => {
      if (!draggingBrightnessRef.current) return;
      e.stopPropagation();
      updateVerticalSlider(e.clientY);
    },
    onPointerUp: (e) => {
      draggingBrightnessRef.current = false;
      e.stopPropagation();
    },
    onPointerCancel: () => { draggingBrightnessRef.current = false; },
  };

  let translateY;
  if (isOpen && !isDragging) translateY = 0;
  else if (isOpen && isDragging) translateY = Math.min(0, dragOffset);
  else if (!isOpen && isDragging) translateY = -PANEL_HEIGHT + Math.min(PANEL_HEIGHT, dragOffset);
  else translateY = -PANEL_HEIGHT;

  const backdropOpacity = isOpen
    ? (isDragging ? Math.max(0, 1 + (translateY / PANEL_HEIGHT)) : 1)
    : (isDragging ? Math.min(1, dragOffset / PANEL_HEIGHT) : 0);

  const isActive = isOpen || isDragging;

  const sipColors = SIP_STATUS_COLORS;

  const iosBlue = '#32ade6';
  const iosPurple = '#af52de';
  const iosOrange = '#ff9500';

  const currentStatus = statusList.find(s => s.key === agentStatus) || statusList[0];

  return (
    <div style={{ ...styles.overlay, ...(isActive ? styles.overlayActive : {}) }}>
      <div
        style={{ ...styles.backdrop, opacity: backdropOpacity }}
        onPointerDown={isOpen ? onClose : undefined}
      />

      <div
        style={{
          ...styles.panel,
          transform: `translateY(${translateY}px)`,
          transition: isDragging ? 'none' : 'transform 0.32s cubic-bezier(0.25, 0.8, 0.3, 1)',
          visibility: isActive ? 'visible' : 'hidden',
        }}
        onPointerDown={isOpen ? onDismissDrag?.pointerDown : undefined}
        onPointerMove={isOpen ? onDismissDrag?.pointerMove : undefined}
        onPointerUp={isOpen ? onDismissDrag?.pointerUp : undefined}
        onPointerCancel={isOpen ? onDismissDrag?.pointerUp : undefined}
      >
        <div style={{ ...styles.grabber, opacity: isActive ? 1 : 0 }} />

        {/* ROW 1: status info (left) + SIP icons (right) */}
        <div style={styles.row}>
          <div style={styles.tile}>
            <button
              type="button"
              style={styles.rowButton}
              onPointerDown={(e) => {
                e.preventDefault(); e.stopPropagation();
                const idx = statusList.findIndex(s => s.key === agentStatus);
                const next = statusList[(idx + 1) % statusList.length];
                onAgentStatusChange(next.key);
              }}
            >
              <div style={{
                ...styles.infoIcon,
                background: statusColors[agentStatus] || statusColors.offline,
                boxShadow: `0 0 10px ${statusColors[agentStatus] || statusColors.offline}60`,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div style={styles.infoText}>
                <span style={styles.infoLabel}>Agent</span>
                <span style={styles.infoValue}>{currentStatus.label}</span>
              </div>
            </button>

            <div style={styles.infoRow}>
              <div style={{
                ...styles.infoIcon,
                background: keypadSound ? iosBlue : 'rgba(110, 110, 125, 0.6)',
                boxShadow: keypadSound ? `0 0 10px ${iosBlue}60` : 'none',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                  <circle cx="6" cy="6" r="2"/><circle cx="12" cy="6" r="2"/><circle cx="18" cy="6" r="2"/>
                  <circle cx="6" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="18" cy="12" r="2"/>
                  <circle cx="6" cy="18" r="2"/><circle cx="12" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>
                </svg>
              </div>
              <div style={styles.infoText}>
                <span style={styles.infoLabel}>Keypad</span>
                <span style={styles.infoValue}>{keypadSound ? 'On' : 'Off'}</span>
              </div>
            </div>

            <div style={styles.infoRow}>
              <div style={{
                ...styles.infoIcon,
                background: ringtoneOn ? iosBlue : 'rgba(110, 110, 125, 0.6)',
                boxShadow: ringtoneOn ? `0 0 10px ${iosBlue}60` : 'none',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                </svg>
              </div>
              <div style={styles.infoText}>
                <span style={styles.infoLabel}>Ringtone</span>
                <span style={styles.infoValue}>{ringtoneOn ? 'On' : 'Muted'}</span>
              </div>
            </div>
          </div>

          <div style={styles.sipIconTile}>
            <button
              type="button"
              style={styles.sipBtn}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onSipConnect(); }}
              title="Connect"
            >
              <div style={{
                ...styles.sipBtnCircle,
                background: sipStatus === 'connected' ? sipColors.connected : 'rgba(255,255,255,0.18)',
                boxShadow: sipStatus === 'connected' ? `0 0 14px ${sipColors.connected}80` : 'none',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                </svg>
              </div>
            </button>

            <div />

            <button
              type="button"
              style={styles.sipBtn}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onSipDisconnect(); }}
              title="Disconnect"
            >
              <div style={{
                ...styles.sipBtnCircle,
                background: sipStatus === 'disconnected' ? sipColors.disconnected : 'rgba(255,255,255,0.18)',
                boxShadow: sipStatus === 'disconnected' ? `0 0 14px ${sipColors.disconnected}80` : 'none',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ pointerEvents: 'none' }}>
                  <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" fill="#fff"/>
                  <line x1="3" y1="3" x2="21" y2="21" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
              </div>
            </button>

            <button
              type="button"
              style={styles.sipBtn}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onSipBreak(); }}
              title="Break"
            >
              <div style={{
                ...styles.sipBtnCircle,
                background: sipStatus === 'break' ? sipColors.break : 'rgba(255,255,255,0.18)',
                boxShadow: sipStatus === 'break' ? `0 0 14px ${sipColors.break}80` : 'none',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                  <rect x="6" y="5" width="4" height="14" rx="1"/>
                  <rect x="14" y="5" width="4" height="14" rx="1"/>
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* ROW 2: toggles (wider) + brightness (narrower) */}
        <div style={styles.row2}>
          <div style={styles.tile}>
            <button
              type="button"
              style={styles.rowButton}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onThemeToggle(); }}
            >
              <div style={{
                ...styles.infoIcon,
                background: theme === 'dark' ? iosPurple : iosOrange,
              }}>
                {theme === 'dark' ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <circle cx="12" cy="12" r="4"/>
                    <g stroke="#fff" strokeWidth="2" strokeLinecap="round">
                      <line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/>
                      <line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>
                      <line x1="4.9" y1="4.9" x2="7" y2="7"/><line x1="17" y1="17" x2="19.1" y2="19.1"/>
                      <line x1="4.9" y1="19.1" x2="7" y2="17"/><line x1="17" y1="7" x2="19.1" y2="4.9"/>
                    </g>
                  </svg>
                )}
              </div>
              <div style={styles.infoText}>
                <span style={styles.infoLabel}>Theme</span>
                <span style={styles.infoValue}>{theme === 'dark' ? 'Dark' : 'Light'}</span>
              </div>
            </button>

            <button
              type="button"
              style={styles.rowButton}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onRingtoneToggle(); }}
            >
              <div style={{
                ...styles.infoIcon,
                background: ringtoneOn ? iosBlue : 'rgba(110, 110, 125, 0.6)',
              }}>
                {ringtoneOn ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <path d="M20 18.69L7.84 6.14 5.27 3.49 4 4.76l2.8 2.8c-.52.99-.8 2.16-.8 3.42v5l-2 2v1h13.73l2 2L21 19.72l-1-1.03zM12 22c1.11 0 2-.89 2-2h-4c0 1.11.89 2 2 2z"/>
                  </svg>
                )}
              </div>
              <div style={styles.infoText}>
                <span style={styles.infoLabel}>Ringtone</span>
                <span style={styles.infoValue}>{ringtoneOn ? 'On' : 'Muted'}</span>
              </div>
            </button>

            <button
              type="button"
              style={styles.rowButton}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onKeypadSoundToggle(); }}
            >
              <div style={{
                ...styles.infoIcon,
                background: keypadSound ? iosBlue : 'rgba(110, 110, 125, 0.6)',
              }}>
                {keypadSound ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <circle cx="6" cy="6" r="2"/><circle cx="12" cy="6" r="2"/><circle cx="18" cy="6" r="2"/>
                    <circle cx="6" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="18" cy="12" r="2"/>
                    <circle cx="6" cy="18" r="2"/><circle cx="12" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <circle cx="6" cy="6" r="2" opacity="0.5"/><circle cx="12" cy="6" r="2" opacity="0.5"/><circle cx="18" cy="6" r="2" opacity="0.5"/>
                    <circle cx="6" cy="12" r="2" opacity="0.5"/><circle cx="12" cy="12" r="2" opacity="0.5"/><circle cx="18" cy="12" r="2" opacity="0.5"/>
                    <circle cx="6" cy="18" r="2" opacity="0.5"/><circle cx="12" cy="18" r="2" opacity="0.5"/><circle cx="18" cy="18" r="2" opacity="0.5"/>
                    <line x1="3" y1="3" x2="21" y2="21" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
              <div style={styles.infoText}>
                <span style={styles.infoLabel}>Keypad Tone</span>
                <span style={styles.infoValue}>{keypadSound ? 'On' : 'Off'}</span>
              </div>
            </button>
          </div>

          <div
            ref={brightnessRef}
            style={styles.brightnessTile}
            {...sliderHandlers}
          >
            <div style={{ ...styles.brightnessFill, height: `${brightness}%` }} />
            <div style={styles.brightnessIcon}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff">
                <circle cx="12" cy="12" r="4"/>
                <g stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="3" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="21"/>
                  <line x1="3" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="21" y2="12"/>
                  <line x1="5.6" y1="5.6" x2="7" y2="7"/><line x1="17" y1="17" x2="18.4" y2="18.4"/>
                  <line x1="5.6" y1="18.4" x2="7" y2="17"/><line x1="17" y1="7" x2="18.4" y2="5.6"/>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PBXControlCenter;